import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import PageHeader from "src/components/common/PageHeader";
import Button from "src/components/ui/Button";
import CardHeader from "src/components/common/card/CardHeader";
import Card from "src/components/common/card/Card";
import { Badge } from "src/components/ui/Badge";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as adjustmentOrderActions from "src/redux/slices/adjustmentOrderSlice";
import { formatDateTime } from "src/utils/date-time";
import { formatCreatedBy } from "src/utils/common";
import usePlanUsage from "src/hooks/usePlanUsage";

const AdjustmentListPage: React.FC = () => {
  const [limit] = useState(10);

  const { list } = useAppSelector((state) => state.adjustmentOrder);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateAdjustment =
    isTenantAdmin || (permissions?.adjustments || []).includes("create");
  const canEditAdjustment =
    isTenantAdmin || (permissions?.adjustments || []).includes("edit");
  const isRowOwner = (row: any) => {
    const createdById =
      typeof row?.createdBy === "string" ? row.createdBy : row?.createdBy?._id;
    if (!createdById || !user?._id) return false;
    return String(createdById) === String(user._id);
  };
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { planUsage } = usePlanUsage();

  const transactionLimit = planUsage?.plan?.features?.totalTransactions;
  const transactionUsage = planUsage?.usage?.totalTransactions;
  const isPlanExpired = planUsage?.planStatus === "expired";
  const isTransactionLimitReached =
    typeof transactionLimit === "number" &&
    typeof transactionUsage === "number" &&
    transactionUsage >= transactionLimit;
  const disableCreateAdjustment = isPlanExpired || isTransactionLimitReached;

  const getAdjustments = useCallback(
    (page: number) => {
      dispatch(
        adjustmentOrderActions.getAdjustmentOrderListRequest({
          page,
          limit,
        })
      );
    },
    [dispatch, limit]
  );

  useEffect(() => {
    getAdjustments(1);
  }, [getAdjustments]);

  const handlePageChange = (page: number) => getAdjustments(page);

  return (
    <DashboardLayout
      meta={{
        title: "Adjustments | Invyntra",
        description:
          "Track and record all manual stock adjustments in the inventory.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Stock Adjustments"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Adjustments", path: "/app/adjustment/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Adjustment Orders"
            description="Manual stock corrections and physical count updates."
            actions={
              canCreateAdjustment ? (
                <Button
                  variant="primary"
                  onClick={() => navigate("/app/adjustment/create")}
                  disabled={disableCreateAdjustment}
                  title={
                    isPlanExpired
                      ? "Plan expired. Please upgrade."
                      : isTransactionLimitReached
                      ? "Order limit reached. Please upgrade."
                      : undefined
                  }
                >
                  <Plus size={16} className="mr-1" /> Create
                </Button>
              ) : null
            }
          />

          <TableV2
            columns={[
              {
                key: "adjustmentOrderId",
                header: "Adjustment ID",
                accessor: ({ row }) => <span>#{row.adjustmentOrderId}</span>,
              },
              {
                key: "products",
                header: "Products",
                width: "400px",
                accessor: ({ row }) => (
                  <ul className="list-decimal">
                    {row.products?.map((p: any) => (
                      <li key={`${row._id}-${p.productId?._id || p.productId}`}>
                        {p.productId?.name}{" "}
                        <span
                          className={
                            p.direction === "in"
                              ? "text-green-500"
                              : "text-red-500"
                          }
                        >
                          ({p.direction})
                        </span>
                      </li>
                    ))}
                  </ul>
                ),
              },
              { key: "remarks", header: "Remarks" },
              {
                key: "orderStatus",
                header: "Status",
                align: "center",
                accessor: ({ row, key }) =>
                  row[key] ? (
                    <Badge
                      color={row[key] === "completed" ? "green" : "yellow"}
                      text={row[key]}
                    />
                  ) : (
                    "-"
                  ),
              },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Date",
                align: "center",
                accessor: ({ row }) => (
                  <div>
                    {formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A")}
                  </div>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                align: "center",
                accessor: ({ row }) =>
                  canEditAdjustment &&
                  row.orderStatus === "draft" &&
                  (isTenantAdmin || isRowOwner(row)) ? (
                    <button
                      className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                      onClick={() =>
                        navigate(`/app/adjustment/${row._id}/edit`)
                      }
                    >
                      <Pencil size={14} />
                    </button>
                  ) : null,
              },
            ]}
            data={list?.data?.data}
            pagination={list?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdjustmentListPage;
